import { useEffect, useState, useRef } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import client from "@/lib/api";
import { Loader2, X, ImageOff, ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from "lucide-react";

// Authenticated thumbnail — clicking opens the in-app lightbox (no raw blob tab).
export function DispatchPhoto({ dispatchId, photo, onOpen, onDelete }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let objectUrl = null;
    let active = true;
    (async () => {
      try {
        const res = await client.get(`/dispatch/${dispatchId}/photos/${photo.id}`, { responseType: "blob" });
        objectUrl = window.URL.createObjectURL(res.data);
        if (active) setUrl(objectUrl);
      } catch {
        if (active) setFailed(true);
      }
    })();
    return () => { active = false; if (objectUrl) window.URL.revokeObjectURL(objectUrl); };
  }, [dispatchId, photo.id]);

  return (
    <div className="group relative h-20 w-20 shrink-0 overflow-hidden rounded-xl border border-slate-200 bg-slate-50" data-testid={`dispatch-photo-${photo.id}`}>
      {failed ? (
        <div className="grid h-full w-full place-items-center text-slate-400"><ImageOff className="h-5 w-5" /></div>
      ) : url ? (
        <button type="button" onClick={onOpen} data-testid={`open-photo-${photo.id}`} className="h-full w-full">
          <img src={url} alt={photo.filename} className="h-full w-full object-cover transition-transform group-hover:scale-105" />
        </button>
      ) : (
        <div className="grid h-full w-full place-items-center text-slate-300"><Loader2 className="h-4 w-4 animate-spin" /></div>
      )}
      {onDelete && (
        <button onClick={() => onDelete(photo)} data-testid={`delete-photo-${photo.id}`} title="Remove photo"
          className="absolute right-1 top-1 grid h-5 w-5 place-items-center rounded-full bg-black/55 text-white opacity-0 transition-opacity hover:bg-error group-hover:opacity-100">
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}

// Full-screen lightbox (portaled, so it escapes any transformed dialog ancestor)
// with flexible zoom: mouse wheel, pinch, drag-to-pan and a slider.
const MIN_Z = 1, MAX_Z = 4;
const clampZ = (z) => Math.min(MAX_Z, Math.max(MIN_Z, z));

export function PhotoLightbox({ dispatchId, photos, index, onIndex, onClose }) {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const pointers = useRef(new Map());
  const pinch = useRef({ dist: 0, zoom: 1 });
  const drag = useRef(null);
  const total = photos.length;
  const photo = photos[index];

  const resetView = () => { setZoom(1); setPan({ x: 0, y: 0 }); };

  useEffect(() => {
    let obj = null, active = true;
    setLoading(true); setUrl(null); resetView();
    (async () => {
      try {
        const res = await client.get(`/dispatch/${dispatchId}/photos/${photo.id}`, { responseType: "blob" });
        obj = window.URL.createObjectURL(res.data);
        if (active) setUrl(obj);
      } catch { /* ignore */ }
      finally { if (active) setLoading(false); }
    })();
    return () => { active = false; if (obj) window.URL.revokeObjectURL(obj); };
  }, [dispatchId, photo.id]);

  const prev = () => onIndex((index - 1 + total) % total);
  const next = () => onIndex((index + 1) % total);
  const applyZoom = (z) => { const nz = clampZ(z); setZoom(nz); if (nz === 1) setPan({ x: 0, y: 0 }); };

  useEffect(() => {
    const h = (e) => {
      if (e.key === "Escape") { e.stopPropagation(); onClose(); return; }
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea") return;
      if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "+" || e.key === "=") applyZoom(zoom + 0.25);
      else if (e.key === "-" || e.key === "_") applyZoom(zoom - 0.25);
    };
    window.addEventListener("keydown", h, true);
    return () => window.removeEventListener("keydown", h, true);
  });

  const onWheel = (e) => { e.preventDefault(); applyZoom(zoom - e.deltaY * 0.0025 * zoom); };

  const onPointerDown = (e) => {
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      pinch.current = { dist: Math.hypot(a.x - b.x, a.y - b.y) || 1, zoom };
    } else if (zoom > 1) {
      drag.current = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y };
      e.currentTarget.setPointerCapture?.(e.pointerId);
    }
  };
  const onPointerMove = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      applyZoom(pinch.current.zoom * (dist / pinch.current.dist));
    } else if (drag.current) {
      setPan({ x: drag.current.panX + (e.clientX - drag.current.x), y: drag.current.panY + (e.clientY - drag.current.y) });
    }
  };
  const onPointerUp = (e) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinch.current = { dist: 0, zoom };
    if (pointers.current.size === 0) drag.current = null;
  };

  const panning = zoom > 1;
  return (
    <DialogPrimitive.Root open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[100] bg-black/95" />
        <DialogPrimitive.Content
          data-testid="photo-lightbox"
          onOpenAutoFocus={(e) => e.preventDefault()}
          className="fixed inset-0 z-[100] flex flex-col focus:outline-none">
          <DialogPrimitive.Title className="sr-only">Dispatch photo viewer</DialogPrimitive.Title>
          <div className="flex items-center justify-between gap-3 px-4 py-3 text-white">
            <span className="shrink-0 text-sm font-medium" data-testid="lightbox-counter">{index + 1} / {total}</span>
            <div className="flex flex-1 items-center justify-end gap-2">
              <button onClick={() => applyZoom(zoom - 0.25)} disabled={zoom <= MIN_Z} data-testid="lightbox-zoom-out" title="Zoom out"
                className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-white/10 transition-colors hover:bg-white/20 disabled:opacity-30"><ZoomOut className="h-5 w-5" /></button>
              <input type="range" min={MIN_Z} max={MAX_Z} step="0.05" value={zoom} data-testid="lightbox-zoom-slider"
                onChange={(e) => applyZoom(parseFloat(e.target.value))}
                className="h-1 w-28 max-w-[35vw] cursor-pointer accent-white sm:w-40" />
              <button onClick={() => applyZoom(zoom + 0.25)} disabled={zoom >= MAX_Z} data-testid="lightbox-zoom-in" title="Zoom in"
                className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-white/10 transition-colors hover:bg-white/20 disabled:opacity-30"><ZoomIn className="h-5 w-5" /></button>
              <span className="w-11 shrink-0 text-center text-xs tabular-nums text-white/80" data-testid="lightbox-zoom-level">{Math.round(zoom * 100)}%</span>
              <button onClick={onClose} data-testid="lightbox-close" title="Close"
                className="ml-1 grid h-9 w-9 shrink-0 place-items-center rounded-full bg-white/10 transition-colors hover:bg-white/20"><X className="h-5 w-5" /></button>
            </div>
          </div>
          <div className="relative flex flex-1 items-center justify-center overflow-hidden"
            onWheel={onWheel} onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp}
            style={{ touchAction: "none" }}>
            {total > 1 && (
              <button onClick={prev} data-testid="lightbox-prev" className="absolute left-2 top-1/2 z-10 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/25"><ChevronLeft className="h-6 w-6" /></button>
            )}
            {loading ? <Loader2 className="h-8 w-8 animate-spin text-white/70" />
              : url ? (
                <img src={url} alt={photo.filename} data-testid="lightbox-image" draggable={false}
                  onDoubleClick={() => (panning ? resetView() : applyZoom(2))}
                  style={{
                    maxHeight: "88vh", maxWidth: "94vw",
                    transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                    transition: drag.current || pointers.current.size ? "none" : "transform 0.12s ease-out",
                    cursor: panning ? "grab" : "zoom-in",
                  }} />
              )
              : <span className="text-sm text-white/60">Couldn't load this photo.</span>}
            {total > 1 && (
              <button onClick={next} data-testid="lightbox-next" className="absolute right-2 top-1/2 z-10 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/25"><ChevronRight className="h-6 w-6" /></button>
            )}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
